import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { InvestmentReadModel } from '@client/api';
import {
    isDefinedNumber,
    isDefinedString,
    TableCell,
    TableRow,
    Text,
    toIntervalEndDateString,
    toIntervalStartDateString,
    toNumberString
} from '@client/toolkit';

import { InvestmentsPaths } from '../../../investments/investments.router';

interface InvestmentItemProps {
  investment?: InvestmentReadModel;
  isFetching?: boolean;
}

export function InvestmentItem({ investment, isFetching, }: InvestmentItemProps) {
  const { t: tCommon } = useTranslation();
  const { t: tType } = useTranslation('type');
  const navigate = useNavigate();

  const totalArea = useMemo(() => {
    if (investment == null || !Array.isArray(investment.spaces) || investment.spaces.length <= 0) return;
    return investment.spaces.reduce((acc, investmentSpace) => acc + (investmentSpace.space.area?.metric ?? 0), 0)
  }, [investment]);

  const investmentSpaces = useMemo(() => {
    if (investment?.spaces == null || !Array.isArray(investment.spaces) || investment.spaces.length <= 0) return undefined;
    const names = investment.spaces.map((x) => x.space?.name).join(', ');
    const total = investment.spaces.length;
    return `${total} (${names})`
  }, [investment?.spaces]);

  const handleClick = useCallback(() => {
    if (!investment?.id) return;

    navigate(generatePath(InvestmentsPaths.Investment, { id: investment.id }));
  }, [investment?.id, navigate]);

  return (
    <TableRow onClick={handleClick} hover={!!investment?.id}>
      <TableCell loading={isFetching}>
        <Text isEmpty={!isDefinedString(toIntervalStartDateString(investment?.duration))}>
          {toIntervalStartDateString(investment?.duration)}
        </Text>
      </TableCell>

      <TableCell loading={isFetching}>
        <Text as="div" isEmpty={!isDefinedString(investment?.name)}>
          <strong className="whitespace-nowrap">{investment?.name}</strong>
        </Text>
      </TableCell>

      <TableCell loading={isFetching}>
        <Text
          isEmpty={!isDefinedString(investmentSpaces)}
          emptyValue={<span className="italic">{tCommon('none')}</span>}
        >
          {investmentSpaces}
        </Text>
      </TableCell>

      <TableCell loading={isFetching}>
        <Text isEmpty={!isDefinedNumber(totalArea)}>
          {toNumberString(totalArea, undefined, {
            unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
          })}
        </Text>
      </TableCell>

      <TableCell loading={isFetching}>
        <Text isEmpty={!isDefinedString(toIntervalEndDateString(investment?.returnDuration))}>
          {toIntervalEndDateString(investment?.returnDuration)}
        </Text>
      </TableCell>
    </TableRow>
  );
}
